/* eslint-disable no-eval */
import { Component, createRef, Dispatch, MouseEvent } from 'react'
import Unity, { UnityContent } from 'react-unity-webgl'
import { Container, Row, Col, ProgressBar } from 'react-bootstrap'
import LoadingOverlay from 'react-loading-overlay'
import RingLoader from 'react-spinners/RingLoader'

import { ToolboxItem, Part, SceneRoom, LDLItem, FaultMode, SafetyException, SoundEffect, SolverCommand } from '../Types'
import { SafetyMessage } from '../SafetyMessage'
import { getGuid, isString, toBoolean, preventContextMenu, getFromSession, sapphireConsoleCSS as logCSS, removeFromSession } from '../Utils'
import * as Solver from '../Solver'

import Toolbox from './Toolbox'
import ContextMenu from './ContextMenu'
import Dialog from './Dialog'
import { ModuleContext } from '../contexts/ModuleContext'
import { isDebug } from '../App.config'
import { solverCSS } from '../Solver'
import { simMod } from '../Module.config'
import { PartSpecsViewer } from './PartSpecsViewer'
import { SimutechBlue } from '../styles/Theme'
import { SimutechModule } from '../ModuleTypes'
import { LearningLabAction, LearningLabActionType } from '../contexts/LearningLabContext'
import { HubConnection } from '@microsoft/signalr'


interface Props {
  faultMode?: FaultMode
  updateCost: Function
  updateTime: Function
  setStatus: Function
  setSceneRoom: Function
  loadFault: Function
  unityRef: (unity: UnityComponent) => void
  onZapandStop: Function
  currentLab: SimutechModule
  labDispatch: Dispatch<LearningLabAction>
}

interface State {
  unityLoaded: boolean
  progress: number
  isPowerOn: boolean
  isTaggedOut: boolean
  cursorX: number
  cursorY: number
  msgDialogShow: boolean
  msgDialogTitle: string
  msgDialogBody: string
  showToolbox: boolean
  receivedSafetyException: SafetyException | null
  showPartSpecs: boolean
  specsPart: Part
}

class UnityComponent extends Component<Props, State> {
  static contextType = ModuleContext

  runningCurrentFunction = ''
  FirstUpdate = 0
  functionsToRun: string[] = []
  private contextMenu = createRef<ContextMenu>()

  state: State = {
    unityLoaded: false,
    progress: 0,
    isPowerOn: false,
    isTaggedOut: false,
    cursorX: 0,
    cursorY: 0,
    msgDialogShow: false,
    msgDialogTitle: '',
    msgDialogBody: '',
    showToolbox: true,
    receivedSafetyException: null,
    showPartSpecs: false,
    specsPart: {} as Part
  }

  unityContent: UnityContent = new UnityContent(
    simMod.unityConfig,
    simMod.unityLoader
  )

  playSound = (soundEffect: SoundEffect) => {
    isDebug && console.log('Playing sound:', soundEffect)
    this.unityContent.send('SoundManager', soundEffect)
  }

  setCursorPosition = (e: any) => {
    this.setState({
      cursorX: e.pageX,
      cursorY: e.pageY
    })
  }

  RegisterFunction = (functionName: any, name: any) => {
    isDebug && console.log('Registering function', functionName)
    const functionStr = `${functionName} = (jsonArgs) => {
        const toUnity = { function: '${functionName}', args: jsonArgs };
        const stringified = JSON.stringify(toUnity);
        console.log(\`Function name: '${functionName}', Object Name: '${name}', Args: \${stringified}\`);
        unityContent.send('${name}', 'HandleBrowserFunction', stringified);
      }`
    window.eval(functionStr)
  }

  CallFunction = (functionName: any, args: string) => {
    const argArray = JSON.parse(args)
    let argString = ''
    for (let argIndex = 0; argIndex < argArray.length; argIndex++) {
      if (argIndex > 0) {
        argString += ' ,'
      }

      let argument = argArray[argIndex]
      if (isString(argument) === true) {
        argument = `'${argument}'`
        argument = argument.replace(/[\n\r]/g, '\\n')
      }

      argString += argument
    }

    const functionStr = `${functionName}(${argString});`
    const exists = window.eval(`typeof ${functionName} === 'function'`)

    if (exists) {
      window.eval(functionStr)
    }
    else {
      console.warn(`[METHOD] ${functionName} is not found.`)
    }

    return exists
  }

  SendMessageToSolver = (connection: HubConnection, message: string) => {
    if (this.runningCurrentFunction === '') {
      isDebug && console.log(`%cPayload being sent to solver: ${message}`, solverCSS)

      isDebug && console.log(`Connection state is ${connection.state}`)
      connection.invoke(SolverCommand.ProcessMessage, message)
        .catch((err: any) => {
          return console.error(err.toString())
        })

      if (message.indexOf('UpdatePushbutton') >= 0) {
        this.runningCurrentFunction = 'SendMessageToSolver:UpdatePushbutton'
      }
      else if (message.indexOf('DepressDevice') >= 0) {
        this.runningCurrentFunction = 'SendMessageToSolver:DepressDevice'
      }

      if (message.indexOf('PlaceLead') >= 0) {
        this.runningCurrentFunction = 'SendMessageToSolver:PlaceLead'
      }
      else if (message.indexOf('RemoveLead') >= 0) {
        this.runningCurrentFunction = 'SendMessageToSolver:RemoveLead'
      }
    }
    else {
      this.waitForTick(`SendMessageToSolver('${message}')`)
    }
  }

  waitForTick = (functionStr: string) => {
    console.warn('Wait For Tick. ' + functionStr)
    this.functionsToRun.push(functionStr)
  }

  replaceItem = (part: Part) => {
    const partKey = part.partSolverID ? part.partSolverID : part.partID
    const msg = JSON.stringify(
      {
        Id: getGuid(),
        GroupName: this.context.groupName,
        CommandName: SolverCommand.ReplaceItem,
        CommandArguments: { ItemId: partKey }
      })
    this.SendMessageToSolver(this.context.connection, msg)
  }

  viewSpecs = (part: Part) => {
    this.setState({
      showPartSpecs: true,
      specsPart: part
    })
  }

  updateProgress = (progression: number) => {
    const percent = Math.trunc(progression * 100)
    if (percent - this.state.progress > 5 || percent >= 89) {
      console.info(`Unity loading progress: ${percent}%`)
      this.setState({
        progress: percent
      })
    }
  }

  RegisterUnityCallbacks = () => {
    // Built-in Events
    this.unityContent.on('progress', (progression: number) => {
      this.updateProgress(progression)
    })

    this.unityContent.on('SendMessageToSolver', (msg: string) => {
      this.SendMessageToSolver(this.context.connection, msg)
    })

    this.unityContent.on('ChangeScene', async (scene: string) => {
      isDebug && console.log(`%cReceived new scene '${scene}' from Unity. Saving it to context...`, logCSS)
      this.props.labDispatch({ type: LearningLabActionType.SET_CURRENT_LAB, payload: scene as SimutechModule })
      await Solver.loadScene(scene, this.context.connection, this.context.groupName)
      const fault = getFromSession('faultId')
      fault && (await this.props.loadFault(fault))
    })

    this.unityContent.on('SetLockout', (msg: string) => {
      const isLockedOut: boolean = toBoolean(msg)
      this.setLockout(isLockedOut)
    })


    this.unityContent.on('SendLockoutState', (msg: string) => {
      const powerIsOn: boolean = toBoolean(msg)

      this.setState(prevState => ({
        isPowerOn: powerIsOn,
        isTaggedOut: (powerIsOn) ? false : prevState.isTaggedOut
      }))
    })

    this.unityContent.on('SendContextMenu', (msg: string) => {
      isDebug && console.info('Context menu: ', msg)
      const part: Part = JSON.parse(msg)

      if (part.partID === '') {
        isDebug && console.error('Configuration error! Part ID is not defined.')
        this.handleErrorDialog('Configuration error!', 'Part ID is not defined.')
      }
      else {
        setTimeout(() => this.contextMenu.current && this.contextMenu.current.handleContextMenu(this.state.cursorX, this.state.cursorY, part), 100)
      }
    })

    this.unityContent.on('loaded', () => {
      console.log('Unity is loaded.')
      setTimeout(() => {
        this.setState({
          unityLoaded: true,
          progress: 0
        }, () => {
          this.props.setStatus(true) // For top/bottom bars
        })
      }, 2000
      )
    })

    this.unityContent.on('SendSceneRoom', (room: string) => {
      if (!room) throw new Error(`Received invalid Scene Room from Unity: ${room}`)
      const sceneRoom: SceneRoom = room as SceneRoom
      isDebug && console.log(`%cReceived new room ${sceneRoom} from Unity.`, logCSS)
      this.props.setSceneRoom(sceneRoom)
      this.setState({
        showToolbox: (sceneRoom !== SceneRoom.Hallway) ? true : false
        // TODO: Unity must always announce which room we are in for all modules so that we can set this to hide the toolbar by default.
      })

    })

    this.unityContent.on('resize', () => {
      // Now we can act on this...
    })

    this.unityContent.on('quitted', () => {
      console.warn('Unity has quitted.')
      this.setState({
        unityLoaded: false
      })
    })

    this.unityContent.on('error', (message: any) => {
      console.error('ERROR! Unity had an error: ', message)
    })
  }

  UnloadUnity = () => {
    if (this.unityContent) {
      console.log('%cUnloading Unity.', logCSS)
      this.unityContent.remove()
    } else {
      console.warn('Attempted to unload non-existant Unity instance.')
    }
  }

  updateLoggedCostDisplay = (value: string) => this.props.updateCost(value)

  updateLoggedTimeDisplay = (value: string) => this.props.updateTime(value)

  selectTool = (unityObj: string, action: string) => this.unityContent.send(unityObj, action)

  toggleLockout = () => this.setState(prevState => ({ isTaggedOut: !prevState.isTaggedOut }))

  setLockout = (locked: boolean) => this.setState({ isTaggedOut: locked })

  unityAvailable = (): boolean => this.state.unityLoaded && this.unityContent !== null && this.unityContent !== undefined

  solverUpdate = (data: any) => {
    this.unityContent.send('Facade', 'UpdateElectricalCode', this.context.user.electricalCode)

    console.log(`%cReceived update from the solver: ${data}`, solverCSS)

    this.unityContent.send('Facade', 'WebSolverUpdated', data)

    if (this.runningCurrentFunction === '' && this.functionsToRun.length > 0) {
      const currentfunction = this.functionsToRun[0]
      this.functionsToRun.shift()
      console.log('CurrentFunction: ' + currentfunction)
      window.eval('ReactUnityWebGL.' + currentfunction)
      this.FirstUpdate = 0
    }
  }

  timeUpdate = (data: any) => {
    isDebug && console.log(`%cReceived Time Update from Solver. ${data}`, solverCSS)
    this.updateLoggedTimeDisplay(data)
  }

  costUpdate = (data: any) => {
    isDebug && console.log(`%cReceived Cost Update from Solver.`, solverCSS)
    this.playSound(SoundEffect.Cash)
    this.updateLoggedCostDisplay(data)
  }

  messageComplete = (data: any) => {
    isDebug && console.log(`%cFinished Ack.${data}`, solverCSS)
    isDebug && console.log('%cCALL FINISHED ACTION ' + this.runningCurrentFunction, solverCSS)
    this.runningCurrentFunction = ''
    // Execute the next message if there are any in the message queue
    if (this.functionsToRun.length > 0) {
      const currentfunction = this.functionsToRun[0]
      this.functionsToRun.shift()
      isDebug && console.log(`%cCurrentFunction: ${currentfunction}`, solverCSS)
      window.eval('ReactUnityWebGL.' + currentfunction)
      this.FirstUpdate = 0
    }
  }

  safetyException = (data: string) => {
    isDebug && console.log(`%cClient Safety Exception ${data}`, solverCSS)
    const safetyMessage = new SafetyMessage(data)
    this.handleSafetyException(safetyMessage)
    this.unityContent.send('Facade', 'WebSolverError', JSON.stringify(data))
  }

  initialState = (data: any) => {
    isDebug && console.log('%cInitial State received from Solver: %s', solverCSS, data)
    this.unityContent.send('Facade', 'WebInitialPosition', data)
  }

  partState = (data: any) => {
    isDebug && console.log('%cPart State received from Solver: %s', solverCSS, data)
    this.unityContent.send('Facade', 'currentPartState', data)
  }

  RegisterSolverCallbacks = (connection: HubConnection) => {
    isDebug && console.log('%cRegistering Unity Solver Callbacks...', logCSS)

    connection.on('clientSolverUpdate', this.solverUpdate)
    connection.on('clientTimeUpdate', this.timeUpdate)
    connection.on('clientCostUpdate', this.costUpdate)
    connection.on('clientMessageComplete', this.messageComplete)
    connection.on('clientSafetyException', this.safetyException)
    connection.on('clientInitialState', this.initialState)
    connection.on('clientPartState', this.partState)
  }

  UnregisterSolverCallbacks = (connection: HubConnection) => {
    isDebug && console.log('%cUnregistering Unity Solver Callbacks...', logCSS)

    connection.off('clientSolverUpdate', this.solverUpdate)
    connection.off('clientTimeUpdate', this.timeUpdate)
    connection.off('clientCostUpdate', this.costUpdate)
    connection.off('clientMessageComplete', this.messageComplete)
    connection.off('clientSafetyException', this.safetyException)
    connection.off('clientInitialState', this.initialState)
    connection.off('clientPartState', this.partState)
  }

  showMsgDialog = (value: boolean) => {
    this.setState({ msgDialogShow: value })

    if (this.state.receivedSafetyException)
      this.props.onZapandStop(this.state.receivedSafetyException)
  }

  acceptLdl = () => this.verifyDead(this.context.connection, this.context.groupName)


  verifyDead = (conn: HubConnection, grpName: string) => {
    const payload = JSON.stringify({
      Id: getGuid(),
      GroupName: grpName,
      CommandName: SolverCommand.VerifyDeadPowerSupply
    })
    this.SendMessageToSolver(conn, payload)
  }

  handleErrorDialog = (title: string, body: string) => {
    isDebug && console.error('Exception: ', title, body)
    this.setState({
      msgDialogTitle: title,
      msgDialogBody: body,
      msgDialogShow: true
    })
  }




  handleSafetyException = (safetyMessage: SafetyMessage) => {
    console.log(`%cSafety Exception: ${safetyMessage.type}`, logCSS)
    switch (safetyMessage.type) {
      case SafetyException.Zap:
        this.playSound(SoundEffect.Zap)
        break
      default:
        this.playSound(SoundEffect.Message)
    }
    this.handleErrorDialog(safetyMessage.type + ': ' + safetyMessage.title, safetyMessage.message)
    this.setState({ receivedSafetyException: safetyMessage.type })
  }

  removeEventListeners() {
    window.removeEventListener('unload', this.handleUnload)
    document.removeEventListener('contextmenu', this.setCursorPosition)
  }

  addEventListeners() {
    window.addEventListener('unload', this.handleUnload)
    document.addEventListener('contextmenu', this.setCursorPosition)
  }

  handleUnload = async () => {
    removeFromSession('faultId')
    this.UnregisterSolverCallbacks(this.context.connection)
    this.UnloadUnity()
  }

  componentWillUnmount() {
    this.removeEventListeners()
    isDebug && console.log('%cUnity Component unmounting...', logCSS)
    this.handleUnload()
  }

  componentDidMount = async () => {
    isDebug && console.log('%cUnity Component is mounted.', logCSS)
    this.addEventListeners()
    this.props.unityRef(this)
    isDebug && console.log(`%cInitial fault on mount ${this.context.faultID}`, logCSS)
    this.RegisterSolverCallbacks(this.context.connection)
    this.RegisterUnityCallbacks()
    if ((this.props.faultMode && this.props.faultMode === FaultMode.Explore) || simMod.isLab) {
      await this.props.loadFault(this.context.faultID)
    }
  }

  render() {

    const {
      unityLoaded,
      progress,
      isPowerOn,
      isTaggedOut,
      showToolbox,
      msgDialogTitle,
      msgDialogBody
    } = this.state

    const { toolboxItems, ldlInfos } = simMod
    const ldlInfo = ldlInfos.filter((info: LDLItem) => { return info.labs.includes(this.props.currentLab) })[0]
    const tools: ToolboxItem[] = toolboxItems

    if (!this.context.solverAvailable) return (
      <LoadingOverlay
        active={!this.context.solverAvailable}
        spinner={<RingLoader color={SimutechBlue} />}
        classNamePrefix="loader-"
        text={`Loading...`}
      />
    )

    return (
      <>
        {<Unity unityContent={this.unityContent} />}

        {!unityLoaded
          // TODO: When converted to FC, use learning lab context and show loading while currentSectionId is 0
          // || (simMod.isLab && state.)
          && (
            <LoadingOverlay
              active={!unityLoaded}
              spinner={<RingLoader color={SimutechBlue} />}
              classNamePrefix="loader-"
              text={`${progress}%`}
            />
          )}

        <div onContextMenu={(e: MouseEvent) => {
          this.setCursorPosition(e)
          preventContextMenu(e)
        }} className="bottom-bar">
          {unityLoaded &&
            (
              <ContextMenu
                ref={this.contextMenu}
                replaceItem={this.replaceItem}
                viewSpecs={this.viewSpecs}
              />
            )}
          <Container fluid={true}>
            <Row className='align-items-center'>
              <Col>
                <div className='instructions'>
                  <span>{''} </span>
                </div>
              </Col>
              {!unityLoaded &&
                (
                  <Col className='justify-content-center'>
                    <div>
                      <ProgressBar
                        animated={true}
                        striped={true}
                        now={progress}
                        min={0}
                        max={100}
                        label={`${progress}%`}
                        className='progress-bar'
                      />
                    </div>
                  </Col>
                )
              }
              <Col>
                {unityLoaded && showToolbox && (
                  <Toolbox
                    tools={tools}
                    ldlInfo={ldlInfo}
                    selectTool={this.selectTool}
                    toggleLockout={this.toggleLockout}
                    acceptLDL={this.acceptLdl}
                    isPowerOn={isPowerOn}
                    isTaggedOut={isTaggedOut}
                  />
                )}
              </Col>
            </Row>
          </Container>
        </div>

        {this.state.showPartSpecs &&
          (
            <PartSpecsViewer
              part={this.state.specsPart}
              onClose={() => this.setState({ showPartSpecs: false })}
            />
          )
        }

        <Dialog
          title={msgDialogTitle}
          body={msgDialogBody}
          show={this.state.msgDialogShow}
          onHide={() => { this.showMsgDialog(false) }}
        />
      </>
    )
  }
}

export default UnityComponent